var PageController        = require("app/core/PageController"),
    PremiumNoticePageView = require("./PremiumNoticePageView"),
    UpgradeCtaPageView    = require("./UpgradeCtaPageView"),
    deepEqual             = require("fast-deep-equal"),
    get                   = require("object-path").get,
    isIosApp              = require("app/utils/isIosApp"),
    kind                  = require("enyo/kind"),
    logger                = require("enyo/logger"),
    quickMacroize         = require("enyo/macroize").quickMacroize;

module.exports = kind(/** @lends  app.UpgradeRequiredPage.PageController.prototype */ {

    /**
     * @private
     */
    name: "app.UpgradeRequiredPage.PageController",

    /**
     * @private
     */
    kind: PageController,


    /**
     * Implement the logic for loading and displaying the page here.
     *
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the page is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the page failed to activate.
     * @public
     */
    execute: function(activation) {
        var app             = this.app,
            $router         = app.$.router,
            defaultLanguage = app.$.i18nService.getDefaultLanguage(),
            language        = get(activation, "params.language", null) || defaultLanguage,
            redirectPath    = get(activation, "params.redirectPath", null) || $router.getDefaultPath({
                language: language
            });

        this.evaluateLoginStatus()
            .then(this.bindSafely(function(isLoggedIn) {
                var userPrivileges = this.app.$.authService.getUserPrivileges();

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                // If the user is already logged in and has premium
                // subscription, we don't need them to upgrade and we can
                // instead forward them to the desired redirect path.
                if (isLoggedIn === 1
                    && userPrivileges.userStatus === "subscribed"
                    && userPrivileges.subscriptionType === "premium") {
                    activation.resolve(1, function() {
                        $router.redirect(redirectPath);
                    });
                }

                // User does not have a premium subscription, we can show them
                // the upgrade request.
                else {
                    this.updateState(activation.params, redirectPath);

                    // We don't want to allow upgrades in the IOS app.
                    if (isIosApp) {
                        this.updateView(PremiumNoticePageView);
                    }

                    // Everywhere else, it's upselling all the way
                    else {
                        this.updateView(UpgradeCtaPageView);
                    }
                    this.updateDocumentTitle();
                    activation.resolve(1);
                }
            }))

            // Oops
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                activation.resolve(1, function() {
                    $router.redirect(quickMacroize("/{$language}/oops", {
                        language: language
                    }));
                });
            }));
    },

    /**
     * Check the login status of the user.
     *
     * @returns  {Promise}  Returns the authentication status.
     */
    evaluateLoginStatus: function() {
        return new Promise(this.bindSafely(function(resolve) {
            var $authService = this.app.$.authService,
                isLoggedIn   = $authService.isLoggedIn,
                onAuthResult;

            // Login status is known, resolve immediately
            if (isLoggedIn === -1 || isLoggedIn === 1) {
                resolve(isLoggedIn);

                return;
            }

            // Handler for authentication result
            onAuthResult = function() {
                $authService.off("Auth::loggedIn", onAuthResult);
                $authService.off("Auth::loggedOut", onAuthResult);
                resolve($authService.isLoggedIn);
            };

            // Await authentication result
            $authService.on("Auth::loggedIn", onAuthResult);
            $authService.on("Auth::loggedOut", onAuthResult);
        }));
    },

    /**
     * @param  {Object}   params        The params passed to this controller
     * @param  {string=}  redirectPath  The redirect path
     * @private
     */
    updateState: function(params, redirectPath) {
        var state = {};

        state.params       = params;
        state.redirectPath = redirectPath || null;

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }

        // Broadcast state changes to app state
        this.app.$.state.set("pageMediaType", null);
        this.app.$.state.set("pageMediaSlug", null);
        this.app.$.state.set("pageMediaId", null);
    },

    /**
     * Update the document title.
     *
     * @private
     */
    updateDocumentTitle: function() {
        var i18nService = this.app.$.i18nService;

        if (this.app.get("documentTitleOwner") !== this) {
            return;
        }

        this.app.setDocumentTitle(i18nService.getString("app.UpgradeRequiredPage.Document.title"));
    }

});
